import { SubmitStatus, SubmitStatuses } from '@/util/storeUtilities';
import axios from 'axios';
import { createUseStore } from 'wired-react';
import { createStore } from 'wired-store';
import { API_URL } from '~/modules/config';
import { selectData, serializeAxiosError } from '~/modules/utilities/axios_utils';
import { handleException } from '~/modules/utilities/cross_env_utils';
import { validateEmail } from '~/modules/utilities/validation_utils';
import { globalStores } from '@/util/globalStores';

type ValidationResult<T> = Partial<{
  [K in keyof T]: T[K] extends object
    ? Partial<ValidationResult<T[K]>> // Recursively apply to nested objects
    : string | null; // Replace non-objects with string | null
}>;

export type ForgotPasswordStoreState = {
  submitStatus: SubmitStatus;
  notifySuccess: string;
  notifyFailure: string;
  validationErrors: ValidationResult<{ email: string; match: string }>;
  forgotPasswordObj: { email: string };
};

const initialState: ForgotPasswordStoreState = {
  submitStatus: SubmitStatuses.UNINITIALIZED,
  notifySuccess: '',
  notifyFailure: '',
  validationErrors: {},
  forgotPasswordObj: {
    email: '',
  },
};

export const forgotPasswordStore = createStore(
  initialState,
  (set, get) => ({
    updateEmail(email) {
      set(function updateEmail(draft) {
        draft.forgotPasswordObj.email = email;
        draft.validationErrors = validateFields(draft.forgotPasswordObj);
      });
    },
    clearNotifySuccess() {
      set(function clearNotifySuccess(draft) {
        draft.notifySuccess = '';
      });
    },
    clearNotifyFailure() {
      set(function clearNotifyFailure(draft) {
        draft.notifyFailure = '';
      });
    },
    async initiateForgotPassword() {
      try {
        const forgotPasswordObject = selectForgotPasswordObject();

        set(function beginInitiateForgotPassword(draft) {
          draft.submitStatus = SubmitStatuses.SUBMITTING;
        });
        const initiateForgotPasswordResult = await axios
          .post(`${API_URL}/user/initiate-forgot-password`, { email: forgotPasswordObject.email })
          .then(selectData);

        set(function endInitiateForgotPassword(draft) {
          draft.submitStatus = SubmitStatuses.SUBMITTED;
          draft.notifySuccess =
            'If an email exists with the provided email, you will recieve an email with instructions to reset your password.';
          draft.forgotPasswordObj = initialState.forgotPasswordObj;
        });
      } catch (error) {
        handleException(error);
        set(function failureInitiateForgotPassword(draft) {
          draft.submitStatus = SubmitStatuses.FAILED;
          draft.notifyFailure = serializeAxiosError(error).message;
        });
      }
    },
  }),
  {
    storeName: 'forgotPasswordStore',
    attachTo: globalStores(),
    warnAgainstAnonymousActions: IS_DEVELOPMENT,
  },
);

// This hook is used to connect react components to the store
export const useForgotPasswordStore = createUseStore(forgotPasswordStore);

export function selectForgotPasswordStatus(state: ForgotPasswordStoreState = forgotPasswordStore.getState()) {
  return state.submitStatus;
}

export function selectValidationErrors(state: ForgotPasswordStoreState = forgotPasswordStore.getState()) {
  return state.validationErrors;
}

export function selectForgotPasswordObject(state: ForgotPasswordStoreState = forgotPasswordStore.getState()) {
  return state.forgotPasswordObj;
}

export function validateFields(
  forgotPasswordFields: ForgotPasswordStoreState['forgotPasswordObj'],
): ForgotPasswordStoreState['validationErrors'] {
  const validationErrors: ForgotPasswordStoreState['validationErrors'] = {};

  if (!validateEmail(forgotPasswordFields.email)) {
    validationErrors.email = 'Email is not a valid email';
  }

  return validationErrors;
}
